import React, { useState, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { sync, getConfig } from '@capacitor/live-updates';
import './LiveUpdatesDebug.css';

interface DebugLog {
  timestamp: string;
  type: 'info' | 'success' | 'error';
  message: string;
}

const LiveUpdatesDebug: React.FC = () => {
  const [logs, setLogs] = useState<DebugLog[]>([]);
  const [isVisible, setIsVisible] = useState(false);
  const [config, setConfig] = useState<any>(null);
  const [isSyncing, setIsSyncing] = useState(false);

  const addLog = (type: 'info' | 'success' | 'error', message: string) => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [...prev, { timestamp, type, message }]);
  };

  const performSync = async () => {
    if (isSyncing) {
      addLog('error', 'Sync already in progress, skipping duplicate call');
      return;
    }

    setIsSyncing(true);
    addLog('info', 'Starting sync...');

    try {
      const result = await sync();
      addLog('success', `Sync completed: ${JSON.stringify(result, null, 2)}`);
      if (result && result.activeApplicationPathChanged) {
        addLog('info', 'New version available, restart to apply');
      } else {
        addLog('info', 'Already on latest version');
      }
    } catch (error: any) {
      addLog('error', `Sync failed: ${error.message}`);
      addLog('error', `Full error: ${JSON.stringify(error, null, 2)}`);
    } finally {
      setIsSyncing(false);
    }
  };

  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      addLog('info', `Platform: ${Capacitor.getPlatform()}`);

      // Get config
      getConfig()
        .then((cfg) => {
          setConfig(cfg);
          addLog('success', `Config loaded: ${JSON.stringify(cfg, null, 2)}`);
        })
        .catch((error: Error) => {
          addLog('error', `Config failed: ${error.message}`);
        });

      // Delay initial sync slightly to ensure component is fully mounted
      const timeoutId = setTimeout(() => {
        performSync();
      }, 500);

      return () => clearTimeout(timeoutId);
    } else {
      addLog('info', 'Running on web platform, Live Updates disabled');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRetrySync = () => {
    addLog('info', 'Manual sync triggered...');
    performSync();
  };

  if (!Capacitor.isNativePlatform()) {
    return null; // Don't show on web
  }

  return (
    <>
      <button
        className="debug-toggle"
        onClick={() => setIsVisible(!isVisible)}
      >
        {isVisible ? 'Hide' : 'Show'} Debug
      </button>

      {isVisible && (
        <div className="debug-panel">
          <div className="debug-header">
            <h3>Live Updates Debug</h3>
            <button onClick={() => setLogs([])}>Clear</button>
            <button onClick={handleRetrySync}>Retry Sync</button>
          </div>

          {config && (
            <div className="debug-config">
              <strong>Config:</strong>
              <pre>{JSON.stringify(config, null, 2)}</pre>
            </div>
          )}

          <div className="debug-logs">
            {logs.map((log, index) => (
              <div key={index} className={`debug-log debug-log-${log.type}`}>
                <span className="debug-timestamp">[{log.timestamp}]</span>
                <span className="debug-message">{log.message}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default LiveUpdatesDebug;
